import Vue from 'vue';
import axios from '@/plugins/axios';

const STORAGE_KEY = 'kot_pwa_enabled';
const DISMISS_KEY = 'kot_pwa_install_dismissed';
const POLL_MS = 60000;

let applied = null;
let applyQueue = Promise.resolve();
let pollStarted = false;
let deferredPrompt = null;

export const pwaState = Vue.observable({
  enabled: false,
  secure: true,
  installed: false,
  ios: false,
  canInstall: false,
  dismissed: false,
  deviceLabel: 'this device',
});

function readStoredEnabled() {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch (err) {
    return false;
  }
}

function writeStoredEnabled(enabled) {
  try {
    localStorage.setItem(STORAGE_KEY, enabled ? '1' : '0');
  } catch (err) {
    // Private mode can block storage. The in-memory flag still applies this visit.
  }
}

function detectIos() {
  const ua = window.navigator.userAgent || '';
  const iPadOs = window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1;
  return /iphone|ipad|ipod/i.test(ua) || iPadOs;
}

function detectInstalled() {
  const standalone = window.matchMedia('(display-mode: standalone)').matches
    || window.matchMedia('(display-mode: fullscreen)').matches;
  return standalone || window.navigator.standalone === true;
}

export function deviceInstallLabel() {
  const width = window.innerWidth || 0;
  if (width >= 1024) return 'this POS computer';
  if (width >= 768) return 'this waiter tablet';
  return 'this waiter phone';
}

function syncEnvironment() {
  pwaState.secure = window.isSecureContext === true;
  pwaState.ios = detectIos();
  pwaState.installed = detectInstalled();
  pwaState.deviceLabel = deviceInstallLabel();
  try {
    pwaState.dismissed = sessionStorage.getItem(DISMISS_KEY) === '1';
  } catch (err) {
    pwaState.dismissed = false;
  }
  document.documentElement.classList.toggle('kot-pwa-standalone', pwaState.installed);
}

function setManifest(enabled) {
  const link = document.querySelector('link[rel="manifest"]');
  if (!enabled) {
    if (link && link.parentNode) link.parentNode.removeChild(link);
  } else if (!link) {
    const next = document.createElement('link');
    next.rel = 'manifest';
    next.href = `${process.env.BASE_URL || '/'}manifest.webmanifest`;
    document.head.appendChild(next);
  }
  ['apple-mobile-web-app-capable', 'mobile-web-app-capable'].forEach((name) => {
    const meta = document.querySelector(`meta[name="${name}"]`);
    if (meta) meta.setAttribute('content', enabled ? 'yes' : 'no');
  });
}

async function registerServiceWorker() {
  if (!('serviceWorker' in navigator) || !window.isSecureContext) return;
  await navigator.serviceWorker.register(`${process.env.BASE_URL || '/'}sw.js`, {
    scope: process.env.BASE_URL || '/',
    updateViaCache: 'none',
  });
}

async function unregisterServiceWorker() {
  if (!('serviceWorker' in navigator)) return;
  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(registrations.map((registration) => registration.unregister()));
  if (window.caches) {
    const keys = await caches.keys();
    await Promise.all(
      keys.filter((key) => key.indexOf('kot-pwa') === 0).map((key) => caches.delete(key)),
    );
  }
  deferredPrompt = null;
  pwaState.canInstall = false;
}

export function applyPwaSetting(enabled) {
  const next = !!enabled;
  writeStoredEnabled(next);
  pwaState.enabled = next;
  setManifest(next);
  syncEnvironment();
  applyQueue = applyQueue
    .then(() => {
      if (applied === next) return null;
      applied = next;
      return next ? registerServiceWorker() : unregisterServiceWorker();
    })
    .catch(() => {
      applied = null;
    });
  return applyQueue;
}

export function dismissInstallPrompt() {
  try {
    sessionStorage.setItem(DISMISS_KEY, '1');
  } catch (err) {
    // Ignore storage failures; the banner stays hidden for this view via the flag below.
  }
  pwaState.dismissed = true;
}

export async function promptPwaInstall() {
  if (!deferredPrompt) return false;
  deferredPrompt.prompt();
  const choice = await deferredPrompt.userChoice;
  deferredPrompt = null;
  pwaState.canInstall = false;
  if (choice && choice.outcome === 'accepted') {
    pwaState.installed = true;
  }
  return !!(choice && choice.outcome === 'accepted');
}

function bindInstallEvents() {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredPrompt = event;
    pwaState.canInstall = true;
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    pwaState.canInstall = false;
    pwaState.installed = true;
  });
  window.addEventListener('resize', () => {
    pwaState.deviceLabel = deviceInstallLabel();
  });
}

async function refreshPwaFlag() {
  try {
    const res = await axios.get('hms/kot-configuration/pwa', {
      params: { _: Date.now() },
    });
    const enabled = !!(res && res.data && res.data.pwa_enabled);
    await applyPwaSetting(enabled);
  } catch (err) {
    await applyPwaSetting(readStoredEnabled());
  }
}

export function bootPwa() {
  if (typeof window === 'undefined' || pollStarted) return;
  pollStarted = true;
  bindInstallEvents();
  syncEnvironment();
  applyPwaSetting(readStoredEnabled());
  refreshPwaFlag();
  window.setInterval(() => {
    if (document.visibilityState === 'visible') refreshPwaFlag();
  }, POLL_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') refreshPwaFlag();
  });
}
